import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Space } from 'antd';
import { SearchOutlined, EyeOutlined, UserSwitchOutlined, FileExcelOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import api from '../services/api';
import './css/searchComponent.css';
import { useUser } from '../contexts/UserContext';

const { Search } = Input;
const { Option } = Select;

const isMobile = window.innerWidth <= 768;
const mobileSearchClass = isMobile ? 'custom-search-mobile' : '';

const Players = () => {
  const [players, setPlayers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50', '100'] });
  const [searchText, setSearchText] = useState('');
  const [exporting, setExporting] = useState(false);
  const [form] = Form.useForm();
  const user = useUser().user;

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await api.get('/players/export', { params: { search: searchText } });
      const rows = r.data?.data || [];
      if (rows.length === 0) {
        message.warning('Nenhum jogador pra exportar com esse filtro');
        return;
      }
      const sheetRows = rows.map(p => ({
        ID: p.id,
        Nome: p.name || '',
        Telefone: p.phone_number || '',
        Posição: getPositionText(p.position),
        Tipo: getPlayerTypeText(p.type),
        'Nível técnico': p.technical_level ? `${p.technical_level} — ${getLevelText(p.technical_level).replace(/^[\d️⃣🔟\s]+/, '').trim()}` : '',
        Time: p.customer_name || '',
        'Customer ID': p.customer_id || '',
        Status: p.status || '',
        'Cadastrado em': p.created_at ? new Date(p.created_at).toLocaleDateString('pt-BR') : '',
      }));
      const ws = XLSX.utils.json_to_sheet(sheetRows);
      // Auto-width baseado no maior valor de cada coluna
      const cols = Object.keys(sheetRows[0]);
      ws['!cols'] = cols.map(k => ({
        wch: Math.min(60, Math.max(k.length, ...sheetRows.map(r => String(r[k] || '').length)) + 2),
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Jogadores');
      const stamp = new Date().toISOString().slice(0, 10);
      const fname = searchText
        ? `jogadores_${stamp}_filtro-${searchText.replace(/[^\w]+/g, '-')}.xlsx`
        : `jogadores_${stamp}.xlsx`;
      XLSX.writeFile(wb, fname);
      message.success(`${rows.length} jogador(es) exportado(s)${r.data?.capped ? ' (limite 20k atingido)' : ''}`);
    } catch (err) {
      message.error(err?.response?.data?.message || 'Erro ao exportar');
    } finally {
      setExporting(false);
    }
  };

  const fetchPlayers = async (page = 1, search = '', pageSize = 5) => {
    setLoading(true);
    try {
      const response = await api.get('/players', {
        params: { page, limit: pageSize, search }
      });
      setPlayers(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.total, current: page, pageSize }));
    } catch (error) {
      message.error('Erro ao carregar jogadores');
    }
    setLoading(false);
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers', { params: { limit: 1000 } });
      setCustomers(response.data.data);
    } catch (error) {
      message.error('Erro ao carregar times');
    }
  };

  useEffect(() => {
    fetchPlayers();
    fetchCustomers();
  }, []);

  const handleTableChange = (pag) => {
    const newPage = pag.pageSize !== pagination.pageSize ? 1 : pag.current;
    fetchPlayers(newPage, searchText, pag.pageSize);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchPlayers(1, value, 5);
  };

  const showModal = (player = null) => {
    setEditingPlayer(player);
    setModalVisible(true);
    if (player) {
      // Converter valores numéricos para garantir que o Select funcione
      const playerData = {
        ...player,
        position: player.position ? parseInt(player.position) : undefined,
        type: player.type ? parseInt(player.type) : undefined,
        technical_level: player.technical_level ? parseInt(player.technical_level) : undefined
      };
      form.setFieldsValue(playerData);
    } else {
      form.resetFields();
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingPlayer) {
        await api.put(`/players/${editingPlayer.id}`, values);
        message.success('Jogador atualizado com sucesso!');
      } else {
        await api.post('/players', values);
        message.success('Jogador criado com sucesso!');
      }
      setModalVisible(false);
      fetchPlayers(pagination.current, searchText);
    } catch (error) {
      message.error('Erro ao salvar jogador');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/players/${id}`);
      message.success('Jogador inativado com sucesso!');
      fetchPlayers(pagination.current, searchText);
    } catch (error) {
      message.error('Erro ao inativar jogador');
    }
  };

  const getPositionText = (position) => {
    return position == 1 ? 'Jogador de linha' : position == 2 ? 'Goleiro' : position;
  };

  const getPlayerTypeText = (type) => {
    return type == 1 ? 'Mensalista' : type == 2 ? 'Avulso' : type;
  };

  const handleImpersonate = async (customerId, gameName) => {
    Modal.confirm({
      title: 'Acessar como Organizador',
      content: `Deseja acessar o sistema como organizador do time "${gameName}"?`,
      okText: 'Sim',
      cancelText: 'Não',
      onOk: async () => {
        try {
          const response = await api.post('/auth/impersonate', { customerId });

          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));

          message.success(`Agora você está acessando como organizador de ${gameName}`);
          window.location.href = '/team-config';
        } catch (error) {
          message.error('Erro ao acessar como organizador: ' + error.response?.data?.message || 'Verifique os dados do organizador');
        }
      }
    });
  };

  const getLevelText = (level) => {
    const levels = {
      1: '1️⃣ Zero noção',
      2: '2️⃣ Muito fraco',
      3: '3️⃣ Fraco',
      4: '4️⃣ Razoável básico',
      5: '5️⃣ Jogador comum',
      6: '6️⃣ Intermediário',
      7: '7️⃣ Bom de bola',
      8: '8️⃣ Bola no pé',
      9: '9️⃣ Quase craque',
      10: '🔟 Nível profissional'
    };
    return levels[level] || '';
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80, sorter: (a, b) => a.id - b.id },
    { title: 'Nome', dataIndex: 'name', key: 'name', sorter: (a, b) => a.name?.localeCompare(b.name) },
    { title: 'Telefone', dataIndex: 'phone_number', key: 'phone_number', sorter: (a, b) => a.phone_number?.localeCompare(b.phone_number) },
    { title: 'Posição', dataIndex: 'position', key: 'position', render: (value) => getPositionText(value), sorter: (a, b) => (a.position || 0) - (b.position || 0) },
    { title: 'Tipo', dataIndex: 'type', key: 'type', render: (value) => getPlayerTypeText(value), sorter: (a, b) => (a.type || 0) - (b.type || 0) },
    { title: 'Time', dataIndex: 'customer_name', key: 'customer_name', sorter: (a, b) => a.customer_name?.localeCompare(b.customer_name) },
    { title: 'Nível', dataIndex: 'technical_level', key: 'technical_level', render: (value) => getLevelText(value), sorter: (a, b) => a.technical_level - b.technical_level },
    { title: 'Status', dataIndex: 'status', key: 'status', sorter: (a, b) => a.status?.localeCompare(b.status) },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {user?.profile === 'ADMINISTRADOR' && (
            <Button
              icon={<UserSwitchOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleImpersonate(record.customer_id, record.customer_name);
              }}
              size="small"
              type="default"
              title="Acessar como organizador"
            />
          )}
          <Button
            title='Ver detalhes do jogador'
            icon={<EyeOutlined />}
            onClick={() => showModal(record)}
            size="small"
          />
          {/* <Popconfirm
            title="Tem certeza que deseja inativar este jogador?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button icon={<DeleteOutlined />} danger size="small" />
          </Popconfirm> */}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{
        marginBottom: 16,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 12 : 0,
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center'
      }}>
        <Search
          className={mobileSearchClass}
          placeholder="Buscar jogadores..."
          allowClear
          enterButton={<SearchOutlined />}
          onSearch={handleSearch}
          style={{ width: isMobile ? '100%' : 300 }}
        />
        <Button
          icon={<FileExcelOutlined />}
          onClick={handleExport}
          loading={exporting}
          size={isMobile ? 'large' : 'middle'}
          title={searchText ? `Exporta todos os jogadores que casam com "${searchText}"` : 'Exporta todos os jogadores'}
        >
          Exportar XLS
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={players}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
      />

      <Modal
        title={editingPlayer ? 'Consultar Jogador' : 'Novo Jogador'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={isMobile ? '95vw' : 600}
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item name="name" label="Nome" rules={[{ required: true }]}>
            <Input disabled />
          </Form.Item>
          <Form.Item name="phone_number" label="Telefone" rules={[{ required: true }]}>
            <Input disabled/>
          </Form.Item>
          <Form.Item name="position" label="Posição" rules={[{ required: true }]}>
            <Select disabled>
              <Option value={1}>Jogador de linha</Option>
              <Option value={2}>Goleiro</Option>
            </Select>
          </Form.Item>
          {!editingPlayer ? (
            <Form.Item name="customer_id" label="Time" rules={[{ required: true }]}>
              <Select placeholder="Selecione um time" disabled >
                {customers.map(customer => (
                  <Option key={customer.id} value={customer.id}>
                    {customer.game_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          ) : (
            <Form.Item label="Time">
              <Input value={editingPlayer.customer_name} disabled />
            </Form.Item>
          )}
          <Form.Item name="type" label="Tipo" rules={[{ required: true }]}>
            <Select disabled>
              <Option value={1}>Mensalista</Option>
              <Option value={2}>Avulso</Option>
            </Select>
          </Form.Item>
          <Form.Item name="technical_level" label="Nível Técnico">
            <Select disabled>
              <Option value={1}>1️⃣ Zero noção</Option>
              <Option value={2}>2️⃣ Muito fraco</Option>
              <Option value={3}>3️⃣ Fraco</Option>
              <Option value={4}>4️⃣ Razoável básico</Option>
              <Option value={5}>5️⃣ Jogador comum</Option>
              <Option value={6}>6️⃣ Intermediário</Option>
              <Option value={7}>7️⃣ Bom de bola</Option>
              <Option value={8}>8️⃣ Bola no pé</Option>
              <Option value={9}>9️⃣ Quase craque</Option>
              <Option value={10}>🔟 Nível profissional</Option>
            </Select>
          </Form.Item>
          {editingPlayer && (
            <Form.Item name="status" label="Status">
              <Select disabled>
                <Option value="ATIVO">Ativo</Option>
                <Option value="INATIVO">Inativo</Option>
                <Option value="SUSPENSO">Suspenso</Option>
                <Option value="AFASTADO">Afastado</Option>
              </Select>
            </Form.Item>
          )}
          <Form.Item style={{marginTop: 28}}>
            <Space>
              {/* <Button type="primary" htmlType="submit">
                Salvar
              </Button> */}
              <Button onClick={() => setModalVisible(false)}>
                Sair
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Players;